/**
 * WithSkreen - LinkedIn Data Analyzer for Visa Interview Preparation
 *
 * A comprehensive tool to analyze LinkedIn data exports and identify
 * potential red flags, timeline issues, and content concerns that may
 * arise during visa interviews.
 *
 * Usage:
 * 1. Export your LinkedIn data (Settings > Data Privacy > Get a copy of your data)
 * 2. Load the CSV files using the parsers
 * 3. Run the analyzers to get a comprehensive report
 *
 * @example
 * ```typescript
 * import { analyzeLinkedInData, parsePositions, parseProfile } from 'withskreen';
 *
 * const positions = parsePositions(positionsCSV);
 * const profile = parseProfile(profileCSV);
 * // ... parse other files
 *
 * const analysis = analyzeLinkedInData({
 *   profile,
 *   positions,
 *   education,
 *   connections,
 *   jobApplications,
 *   articles,
 *   messages,
 *   publications,
 *   causes,
 *   projects,
 *   recommendations,
 * });
 *
 * console.log(analysis.summary);
 * ```
 */

// Types
export type {
  Severity,
  RedFlagCategory,
  RedFlag,
  Position,
  Education,
  Connection,
  JobApplication,
  Profile,
  Article,
  Share,
  Comment,
  Message,
  Publication,
  Cause,
  Project,
  Recommendation,
  TimelineEntry,
  TimelineAnalysis,
  ContentAnalysis,
  ContentFlag,
  ContentFlagCategory,
  LinkedInData,
  VisaAnalysisResult,
  ContentPatterns,
} from './types.js';

// Parsers
export {
  parsePositions,
  parseEducation,
  parseConnections,
  parseJobApplications,
  parseProfile,
  parseMessages,
  parsePublications,
  parseCauses,
  parseProjects,
  parseRecommendations,
  parseArticle,
  parseScreeningResponses,
  parseShares,
  parseComments,
} from './parsers/linkedin.js';

// Analyzers
export {
  analyzeRedFlags,
  calculateOverallRisk,
  generateRecommendedActions,
  type RedFlagConfig,
} from './analyzers/redflags.js';

export {
  analyzeContent,
  findPositiveIndicators,
} from './analyzers/content.js';

export {
  buildTimeline,
  formatTimeline,
  getUSExperienceYears,
  getNonUSExperienceYears,
  getLongestGap,
  generateTimelineQuestions,
} from './analyzers/timeline.js';

// Main analysis function
import type { LinkedInData, VisaAnalysisResult, Severity } from './types.js';
import { analyzeRedFlags, calculateOverallRisk, generateRecommendedActions } from './analyzers/redflags.js';
import { analyzeContent, findPositiveIndicators } from './analyzers/content.js';
import { buildTimeline } from './analyzers/timeline.js';

/**
 * Perform a complete analysis of LinkedIn data for visa interview preparation
 */
export function analyzeLinkedInData(data: LinkedInData): VisaAnalysisResult {
  // Build timeline
  const timeline = buildTimeline(data.positions, data.education);

  // Analyze for red flags
  const redFlags = analyzeRedFlags(data, timeline);

  // Analyze content
  const contentAnalysis = analyzeContent(
    data.articles,
    data.messages,
    data.publications,
    data.profile,
    data.recommendations,
    data.causes
  );

  // Combine red flags from content analysis
  const allRedFlags = [
    ...redFlags,
    ...contentAnalysis.flaggedItems.map(item => ({
      severity: item.severity,
      category: item.category as any, // ContentFlagCategory overlaps with RedFlagCategory
      description: item.reason,
      evidence: item.excerpt,
      recommendation: item.recommendation,
      dateRange: item.date ? { start: item.date.toISOString() } : undefined,
    })),
  ];

  // Calculate overall risk
  const overallRiskLevel = calculateCombinedRisk(
    calculateOverallRisk(redFlags),
    contentAnalysis.overallRisk
  );

  // Generate recommended actions
  const recommendedActions = generateRecommendedActions(allRedFlags);

  // Find positive indicators
  const positiveIndicators = findPositiveIndicators(
    data.articles,
    data.profile,
    data.messages
  );

  // Build summary
  const criticalFlags = allRedFlags.filter(f => f.severity === 'red').length;
  const warningFlags = allRedFlags.filter(f => f.severity === 'orange').length;

  return {
    redFlags: allRedFlags,
    timeline,
    contentAnalysis,
    linkedInData: data,
    summary: {
      totalRedFlags: allRedFlags.length,
      criticalFlags,
      warningFlags,
      positiveIndicators: positiveIndicators.length,
      overallRiskLevel,
      recommendedActions,
    },
  };
}

/**
 * Calculate combined risk from multiple analyses
 */
function calculateCombinedRisk(risk1: Severity, risk2: Severity): Severity {
  if (risk1 === 'red' || risk2 === 'red') return 'red';
  if (risk1 === 'orange' || risk2 === 'orange') return 'orange';
  return 'green';
}

/**
 * Generate a human-readable report from the analysis
 */
export function generateReport(result: VisaAnalysisResult): string {
  const lines: string[] = [];

  lines.push('╔════════════════════════════════════════════════════════════════╗');
  lines.push('║         WITHSKREEN - VISA INTERVIEW PREPARATION REPORT        ║');
  lines.push('╚════════════════════════════════════════════════════════════════╝');
  lines.push('');

  // Overall Assessment
  const riskEmoji = result.summary.overallRiskLevel === 'red' ? 'HIGH RISK' :
                    result.summary.overallRiskLevel === 'orange' ? 'MODERATE RISK' : 'LOW RISK';
  lines.push(`OVERALL ASSESSMENT: ${riskEmoji}`);
  lines.push('');

  // Summary Stats
  lines.push('SUMMARY');
  lines.push('─'.repeat(64));
  lines.push(`Total Issues Found: ${result.summary.totalRedFlags}`);
  lines.push(`  - Critical (Red): ${result.summary.criticalFlags}`);
  lines.push(`  - Warnings (Orange): ${result.summary.warningFlags}`);
  lines.push(`Positive Indicators: ${result.summary.positiveIndicators}`);
  lines.push('');

  // Timeline Summary
  lines.push('CAREER TIMELINE');
  lines.push('─'.repeat(64));
  lines.push(`Total Positions: ${result.timeline.positionCount}`);
  lines.push(`Career Span: ${formatDays(result.timeline.totalCareerSpanDays)}`);
  lines.push(`Average Tenure: ${formatDays(result.timeline.averageTenureDays)}`);
  lines.push(`US Experience: ${formatDays(result.timeline.totalUSExperienceDays)}`);
  lines.push(`Non-US Experience: ${formatDays(result.timeline.totalHomeCountryExperienceDays)}`);
  lines.push(`Employment Gaps: ${result.timeline.gaps.length}`);
  lines.push(`Position Overlaps: ${result.timeline.overlaps.length}`);
  lines.push(`Short Tenures (<12mo): ${result.timeline.shortTenureCount}`);
  lines.push('');

  // Critical Issues
  if (result.summary.criticalFlags > 0) {
    lines.push('CRITICAL ISSUES (Require Immediate Attention)');
    lines.push('─'.repeat(64));
    const criticalFlags = result.redFlags.filter(f => f.severity === 'red');
    for (const flag of criticalFlags) {
      lines.push(`[!] ${flag.description}`);
      lines.push(`    Category: ${flag.category}`);
      lines.push(`    Evidence: ${truncate(flag.evidence, 100)}`);
      lines.push(`    Action: ${flag.recommendation}`);
      lines.push('');
    }
  }

  // Warnings
  if (result.summary.warningFlags > 0) {
    lines.push('WARNINGS (Should Be Addressed)');
    lines.push('─'.repeat(64));
    const warningFlags = result.redFlags.filter(f => f.severity === 'orange');
    for (const flag of warningFlags) {
      lines.push(`[*] ${flag.description}`);
      lines.push(`    Category: ${flag.category}`);
      lines.push(`    Evidence: ${truncate(flag.evidence, 100)}`);
      lines.push(`    Action: ${flag.recommendation}`);
      lines.push('');
    }
  }

  // Content Analysis Summary
  if (result.contentAnalysis.flaggedItems.length > 0) {
    lines.push('CONTENT ANALYSIS');
    lines.push('─'.repeat(64));
    lines.push(result.contentAnalysis.summary);
    lines.push('');
  }

  // Recommended Actions
  lines.push('RECOMMENDED ACTIONS');
  lines.push('─'.repeat(64));
  for (let i = 0; i < result.summary.recommendedActions.length; i++) {
    lines.push(`${i + 1}. ${result.summary.recommendedActions[i]}`);
  }
  lines.push('');

  // Footer
  lines.push('─'.repeat(64));
  lines.push('This report is for preparation purposes only and does not');
  lines.push('constitute legal advice. Consult an immigration attorney');
  lines.push('for professional guidance.');
  lines.push('─'.repeat(64));

  return lines.join('\n');
}

/**
 * Format days to human-readable duration
 */
function formatDays(days: number): string {
  if (days < 30) return `${days} days`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} months`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years} years`;
  return `${years} years ${remainingMonths} months`;
}

/**
 * Truncate string to max length
 */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
}

/**
 * Create an empty LinkedIn data structure for initialization
 */
export function createEmptyLinkedInData(): LinkedInData {
  return {
    profile: null,
    positions: [],
    education: [],
    connections: [],
    jobApplications: [],
    articles: [],
    messages: [],
    publications: [],
    causes: [],
    projects: [],
    recommendations: [],
    shares: [],
    comments: [],
  };
}
